import React, { forwardRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { XIcon, LeftChevronIcon, RightChevronIcon } from '../../atoms/Icons';
import DeviceDataChart from '../DeviceDataChart';
import './DeviceConnectivityModal.scss';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const DEVICE_DATA_SUMMARIES_QUERY = gql`
  query DeviceDataSummaries($deviceId: uuid!, $after: bigint!, $before: bigint!, $timeZone: String!) {
    device(where: { id: { _eq: $deviceId } }) {
      data_summaries(args: { p_after: $after, p_before: $before, p_interval: "hour", p_time_zone: $timeZone }) {
        avg_time_to_upload
        total_duration
        start_timestamp
        end_timestamp
      }
    }
  }
`;

const DeviceConnectivityModal = forwardRef(function DeviceConnectivityModal(
  {
    className = '',
    isOpen = false,
    onClose = () => {},
    title = 'Device Connectivity (Past 2 Weeks)',
    devices = [],
    initialDeviceIndex = 0,
  },
  ref,
) {
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(initialDeviceIndex);

  // Calculate date range for past 2 weeks - memoized to prevent re-renders
  const { after, before, timeZone } = useMemo(() => {
    const now = dayjs.tz().startOf('hour'); // Use current time, not start of day
    const twoWeeksAgo = now.subtract(14, 'day');

    const afterTimestamp = twoWeeksAgo.unix();
    const beforeTimestamp = now.unix();

    // Validate that we have proper Unix timestamps
    if (typeof afterTimestamp !== 'number' || typeof beforeTimestamp !== 'number') {
      console.error('Invalid timestamps generated:', { afterTimestamp, beforeTimestamp });
      return { after: 0, before: 0, timeZone: 'UTC' };
    }

    return {
      after: afterTimestamp,
      before: beforeTimestamp,
      timeZone: dayjs.tz.guess() || 'UTC',
    };
  }, []);

  // Get current device info
  const currentDevice = devices[currentDeviceIndex] || null;
  const currentDeviceId = currentDevice?.id;
  const currentDeviceSerial = currentDevice?.serial;
  const currentFeedLineName = currentDevice?.feedLineName;

  // Fetch data for current device
  const {
    data: deviceData,
    loading: chartLoading,
    error: chartError,
  } = useQuery(DEVICE_DATA_SUMMARIES_QUERY, {
    variables: {
      deviceId: currentDeviceId,
      after,
      before,
      timeZone: timeZone, // Assuming dayjs.tz.guess() provides the correct timezone
    },
    skip: !currentDeviceId || !isOpen,
    fetchPolicy: 'cache-first',
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });
  if (chartError) {
    throw new Error(chartError);
  }

  const dataSummaries = useMemo(() => {
    if (!deviceData?.device?.[0]?.data_summaries) return [];
    return deviceData.device[0].data_summaries;
  }, [deviceData]);

  // Navigation functions
  const goToPreviousDevice = () => {
    if (currentDeviceIndex > 0) {
      setCurrentDeviceIndex(currentDeviceIndex - 1);
    }
  };

  const goToNextDevice = () => {
    if (currentDeviceIndex < devices.length - 1) {
      setCurrentDeviceIndex(currentDeviceIndex + 1);
    }
  };

  // Update current device index when modal opens with a specific device
  useEffect(() => {
    if (isOpen && initialDeviceIndex !== currentDeviceIndex) {
      setCurrentDeviceIndex(initialDeviceIndex);
    }
  }, [isOpen, initialDeviceIndex]); // Removed currentDeviceIndex dependency

  // Modal lifecycle management
  useEffect(() => {
    if (ref?.current) {
      if (isOpen) {
        ref.current.showModal();
      } else {
        ref.current.close();
      }
    }
  }, [isOpen, ref, currentDeviceSerial]);

  if (chartError) {
    throw new Error('Error loading device data:', chartError);
  }

  const handleClose = () => {
    if (ref?.current) {
      ref.current.close();
    }
    onClose();
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Navigation button states
  const isFirstDevice = currentDeviceIndex === 0;
  const isLastDevice = currentDeviceIndex === devices.length - 1;

  return (
    <dialog className={`DeviceConnectivityModal ${className}`} ref={ref} onClose={handleClose}>
      <div className="DeviceConnectivityModal-overlay">
        <div className="DeviceConnectivityModal-container">
          <div className="DeviceConnectivityModal-header">
            <div className="DeviceConnectivityModal-titleSection">
              <h3 className="DeviceConnectivityModal-title">{title}</h3>
            </div>
            <XIcon className="DeviceConnectivityModal-closeButton" onClick={handleClose} />
          </div>

          <div className="DeviceConnectivityModal-navigation">
            <button
              className={`DeviceConnectivityModal-navButton DeviceConnectivityModal-navButton--left ${
                isFirstDevice ? 'DeviceConnectivityModal-navButton--disabled' : ''
              }`}
              onClick={goToPreviousDevice}
              disabled={isFirstDevice}
              aria-label="Previous device"
            >
              <LeftChevronIcon />
            </button>

            <div className="DeviceConnectivityModal-deviceInfo">
              <span className="DeviceConnectivityModal-deviceCounter">
                Device {currentDeviceIndex + 1} of {devices.length}
              </span>
              <span className="DeviceConnectivityModal-deviceSerial">{currentDeviceSerial}</span>
              <span className="DeviceConnectivityModal-feedLineName">{currentFeedLineName}</span>
            </div>

            <button
              className={`DeviceConnectivityModal-navButton DeviceConnectivityModal-navButton--right ${
                isLastDevice ? 'DeviceConnectivityModal-navButton--disabled' : ''
              }`}
              onClick={goToNextDevice}
              disabled={isLastDevice}
              aria-label="Next device"
            >
              <RightChevronIcon />
            </button>
          </div>

          <div className="DeviceConnectivityModal-content">
            {chartLoading ? (
              <div className="DeviceConnectivityModal-loading">Loading chart data...</div>
            ) : chartError ? (
              <div className="DeviceConnectivityModal-error">Unable to load device data</div>
            ) : dataSummaries.length === 0 ? (
              <div className="DeviceConnectivityModal-error">No data available</div>
            ) : (
              <DeviceDataChart
                dataSummaries={dataSummaries}
                chartHeight={400}
                chartMargin={{ top: 20, bottom: 25, left: 50, right: 25 }}
              />
            )}
          </div>
        </div>
      </div>
    </dialog>
  );
});

DeviceConnectivityModal.propTypes = {
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  devices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      serial: PropTypes.string.isRequired,
      feedLineName: PropTypes.string.isRequired,
    }),
  ),
  initialDeviceIndex: PropTypes.number,
};

export default DeviceConnectivityModal;
